/*
  radon-tiers.js
  Single source of truth for radon tier logic + tier copy.
  Loaded by BOTH sections/radon-test-form.liquid and sections/radon-result-report.liquid.

  Save as: assets/radon-tiers.js

  Copy is transcribed verbatim from radon-tier-copy-spec.html.
  Cigarette values are FIXED per tier (no interpolation) per the copy spec.
*/
(function (global) {
  'use strict';

  var EPA_ACTION_LEVEL = 4.0;

  /*
    The placeholder inside card1Body, swapped for the measured reading at render time.
    It lives HERE, in a plain .js asset, rather than being typed literally in the
    section — Shopify runs Liquid over .liquid files including their <script> blocks,
    so a literal {{RADON_LEVEL}} there would be rendered away to an empty string.
  */
  var LEVEL_TOKEN = '{' + '{RADON_LEVEL}' + '}';

  var CIGARETTE_FOOTNOTE =
    'Based on EPA estimates derived from the BEIR VI lung-cancer risk model. ' +
    'Assumes a never-smoker baseline; the effect is significantly larger for active smokers.';

  var PULL_QUOTE = {
    text: 'Knowing the number is the hardest part. What comes next is straightforward.',
    attrib: '— Radon Report Card Guidance'
  };

  /*
    Tier boundaries (inclusive min, exclusive max):
      Very Low            < 1.0
      Low                 1.0 - 1.9
      Elevated            2.0 - 3.9
      High                4.0 - 7.9   <- EPA action level
      Very High           8.0 - 19.9
      Severely Elevated   20.0 +
  */
  var TIERS = [
    {
      key: 'very-low',
      label: 'Very Low',
      min: 0,
      max: 1.0,
      rangeText: '< 1.0',
      cigarettes: 0,
      aboveEPA: false,
      showProCard: false,
      colors: { base: '#2BA987', soft: '#E9F6F2', line: '#A9DCCD', ink: '#1A6E58' },

      tagSuffix: 'Below EPA Action Threshold',
      headline: 'Your radon level is very low.',
      subheadline:
        'This is about as low as indoor radon gets — well below the average home, which sits around 1.3 pCi/L. ' +
        'There’s nothing you need to fix right now.',

      card1Title: 'About your number',
      card1Body:
        'Your reading of <strong>{{RADON_LEVEL}} pCi/L</strong> falls in the <strong>Very Low</strong> range — ' +
        'well below the EPA’s action threshold of 4.0 pCi/L, and under the average indoor level of about 1.3 pCi/L. ' +
        'For an indoor test, this is the best-case result.',

      card2Title: 'What this means for your lungs',
      card2Body:
        'At this level, the cigarette-equivalent comparison rounds to <strong>zero</strong> — ' +
        'there’s no meaningful added risk to flag. This is about as clean as an indoor reading gets.',

      card3Title: 'Keep it this way',
      card3Body:
        'Radon can shift over time as a home settles and the seasons change. A simple retest every couple of years ' +
        'confirms your air is still clear. No system or repair is needed at this level.',

      stepEyebrow: 'Recommended first',
      stepTitle: 'Retest in about two years',
      stepBody:
        'Nothing needs fixing right now. The one good habit is to retest every couple of years — and any time you ' +
        'finish a basement, change your heating system, or do major foundation work — since radon can move as a ' +
        'home changes. A test reminder makes it effortless.',
      stepCta: 'Enroll in annual testing',

      proTitle: null,
      proBody: null
    },

    {
      key: 'low',
      label: 'Low',
      min: 1.0,
      max: 2.0,
      rangeText: '1.0–1.9',
      cigarettes: 2,
      aboveEPA: false,
      showProCard: false,
      colors: { base: '#84B547', soft: '#F2F7E9', line: '#C6DCA3', ink: '#55762C' },

      tagSuffix: 'Below EPA Action Threshold',
      headline: 'Your radon level is low.',
      subheadline:
        'Your result sits below the EPA’s action threshold. No action is required right now — just a good ' +
        'reason to keep an eye on it over time.',

      card1Title: 'About your number',
      card1Body:
        'Your reading of <strong>{{RADON_LEVEL}} pCi/L</strong> falls in the <strong>Low</strong> range — below ' +
        'the EPA’s action threshold of 4.0 pCi/L. This is a reassuring result, and most homes that test here ' +
        'don’t need any radon work.',

      card2Title: 'What this means for your lungs',
      card2Body:
        'Breathing your home’s air over many years at this level carries roughly the same long-term lung impact ' +
        'as smoking <strong>about 2 cigarettes a day</strong>. It’s a modest risk — worth knowing, not worth ' +
        'worrying over.',

      card3Title: 'No system needed',
      card3Body:
        'There’s nothing to install or repair at this level. Because radon naturally rises and falls with the ' +
        'seasons, the only smart move is to retest periodically, so a quiet upward drift wouldn’t go unnoticed.',

      stepEyebrow: 'Recommended first',
      stepTitle: 'Retest every couple of years',
      stepBody:
        'No mitigation is needed at this level. Plan to retest every one to two years — especially a winter test, ' +
        'when levels tend to run highest — and after any major home or foundation changes. A test reminder keeps ' +
        'it off your mental to-do list.',
      stepCta: 'Enroll in annual testing',

      proTitle: null,
      proBody: null
    },

    {
      key: 'elevated',
      label: 'Elevated',
      min: 2.0,
      max: 4.0,
      rangeText: '2.0–3.9',
      cigarettes: 4,
      aboveEPA: false,
      showProCard: false,
      colors: { base: '#D9B23A', soft: '#FBF5E4', line: '#EBD79A', ink: '#8A6F16' },

      tagSuffix: 'Below EPA Action Threshold',
      headline: 'Your radon level is elevated, but below the EPA action threshold.',
      subheadline:
        'You’re under the 4.0 pCi/L line where the EPA recommends action — but the EPA does suggest ' +
        'considering a fix anywhere in this range. A winter retest is the right next move.',

      card1Title: 'About your number',
      card1Body:
        'Your reading of <strong>{{RADON_LEVEL}} pCi/L</strong> falls in the <strong>Elevated</strong> range — ' +
        'below the EPA’s 4.0 action threshold, but high enough that the EPA suggests you <strong>consider</strong> ' +
        'reducing it. Many homes in this range sit near the line and tip over it in winter.',

      card2Title: 'What this means for your lungs',
      card2Body:
        'At this level, the long-term impact on your lungs is roughly comparable to smoking ' +
        '<strong>about 4 cigarettes a day</strong> over many years. It’s a meaningful enough risk that it’s ' +
        'worth confirming with a second test.',

      card3Title: 'Easily fixable if it climbs',
      card3Body:
        'If a retest comes back at 4.0 or above, a mitigation system reduces radon by <strong>50–99%</strong> ' +
        '— usually a one-day install that runs quietly for decades. Good to know the fix is simple, should you ' +
        'ever need it.',

      stepEyebrow: 'Recommended first',
      stepTitle: 'Retest before deciding — ideally in winter',
      stepBody:
        'Because you’re close to the action line, the best next step is a second test, ideally during the colder ' +
        'months when radon tends to peak. If that reading lands at 4.0 or higher, reducing your radon becomes the ' +
        'clear recommendation. A winter retest reminder makes this easy.',
      stepCta: 'Enroll in annual testing',

      proTitle: null,
      proBody: null
    },

    {
      key: 'high',
      label: 'High',
      min: 4.0,
      max: 8.0,
      rangeText: '4.0–7.9',
      cigarettes: 8,
      aboveEPA: true,
      showProCard: true,
      colors: { base: '#E69A28', soft: '#FDF4E6', line: '#F2C77A', ink: '#8B5A1A' },

      tagSuffix: 'Above EPA Action Threshold',
      headline: 'Your radon level is above the EPA action threshold.',
      subheadline:
        'This is the level at which the EPA recommends taking action. It’s common, it’s fixable, and you ' +
        'have time to take the right steps.',

      card1Title: 'About your number',
      card1Body:
        'Your reading of <strong>{{RADON_LEVEL}} pCi/L</strong> falls in the <strong>High</strong> range — above ' +
        'the EPA’s action threshold of 4.0 pCi/L. This is the level at which the EPA recommends taking steps to ' +
        'reduce radon.',

      card2Title: 'What this means for your lungs',
      card2Body:
        'Breathing air at this level over time has roughly the same long-term impact on your lungs as smoking ' +
        '<strong>about 8 cigarettes a day</strong>.',

      card3Title: 'Radon is fixable',
      card3Body:
        'A mitigation system reduces radon by <strong>50–99%</strong>. Most install in a single day, run quietly ' +
        'in the background, and last for decades. This is a solvable problem.',

      stepEyebrow: 'Recommended first',
      stepTitle: 'Set up a winter retest',
      stepBody:
        'Your most important next step is a follow-up test, ideally in winter when levels run highest. An annual ' +
        'subscription makes sure you never forget — we send a fresh kit, you test, mail it back, and get an ' +
        'updated report.',
      stepCta: 'Enroll in annual testing',

      proTitle: 'Want a no-obligation visit from a certified local pro?',
      proBody:
        'If your winter retest also reads high, a one-time mitigation install can reduce your radon by ' +
        '<strong>50–99%</strong>. A certified specialist can stop by, look at your home, and answer your ' +
        'questions — with no pressure to buy anything.'
    },

    {
      key: 'very-high',
      label: 'Very High',
      min: 8.0,
      max: 20.0,
      rangeText: '8.0–19.9',
      cigarettes: 15,
      aboveEPA: true,
      showProCard: true,
      colors: { base: '#DC5B3E', soft: '#FCEEEA', line: '#F0B7A7', ink: '#93321C' },

      tagSuffix: 'Above EPA Action Threshold',
      headline: 'Your radon level is well above the EPA action threshold.',
      subheadline:
        'This is a high reading, and it’s worth acting on sooner rather than later. The good news is unchanged: ' +
        'radon at this level is very fixable, and a mitigation system brings it down fast.',

      card1Title: 'About your number',
      card1Body:
        'Your reading of <strong>{{RADON_LEVEL}} pCi/L</strong> falls in the <strong>Very High</strong> range — ' +
        'two to five times the EPA’s 4.0 action threshold. Levels like this clearly warrant a radon reduction ' +
        'system, and the EPA recommends acting promptly.',

      card2Title: 'What this means for your lungs',
      card2Body:
        'At this level, the long-term impact on your lungs is roughly comparable to smoking ' +
        '<strong>about 15 cigarettes a day</strong> — close to a pack a day. This is the strongest reason to ' +
        'move forward with a fix.',

      card3Title: 'Still very fixable',
      card3Body:
        'Even at this level, a mitigation system reduces radon by <strong>50–99%</strong> — often back down ' +
        'near 1–2 pCi/L. Most install in a single day and run quietly for decades. A high number is not a ' +
        'permanent one.',

      stepEyebrow: 'Recommended first',
      stepTitle: 'Confirm, then move toward a fix',
      stepBody:
        'At this level the priority is reducing your radon. A quick follow-up test is reasonable, but you don’t ' +
        'need to wait long to start — the EPA recommends acting promptly when readings are this high. We can rush ' +
        'you a fast follow-up kit and line up your next step.',
      stepCta: 'Enroll in annual testing',

      proTitle: 'A certified local pro is your most direct path down',
      proBody:
        'At a reading this high, a certified mitigation specialist is the most direct route to a lower number. They ' +
        'can visit, assess your home, and answer your questions — with no obligation. A properly installed system ' +
        'typically cuts radon by <strong>50–99%</strong>.'
    },

    {
      key: 'severe',
      label: 'Severely Elevated',
      shortLabel: 'Severely Elev.',
      min: 20.0,
      max: Infinity,
      rangeText: '20+',
      cigarettes: 36,
      aboveEPA: true,
      showProCard: true,
      colors: { base: '#8E2F26', soft: '#F7E9E7', line: '#DDB0AA', ink: '#6B221B' },

      tagSuffix: 'Above EPA Action Threshold',
      headline: 'Your radon level is severely elevated.',
      subheadline:
        'This is among the highest readings we report, and it deserves prompt attention. It is still fixable — a ' +
        'mitigation system is highly effective even at this level — and the right move is to get one underway soon.',

      card1Title: 'About your number',
      card1Body:
        'Your reading of <strong>{{RADON_LEVEL}} pCi/L</strong> falls in the <strong>Severely Elevated</strong> range ' +
        '— five times the EPA action threshold or more. Radon is the number-one cause of lung cancer among people ' +
        'who have never smoked, and the EPA advises acting quickly at levels like this. This isn’t cause for ' +
        'panic, but it is cause for prompt action.',

      card2Title: 'What this means for your lungs',
      card2Body:
        'At this level, the long-term impact on your lungs is roughly comparable to smoking ' +
        '<strong>about 36 cigarettes a day</strong> — nearly two packs a day. That comparison is the clearest ' +
        'signal that reducing your radon should be a near-term priority.',

      card3Title: 'Highly effective to fix',
      card3Body:
        'Mitigation works even at severe levels, routinely cutting radon by <strong>50–99%</strong> — often ' +
        'from the twenties back down to low single digits. Systems install in about a day and run quietly for decades. ' +
        'The fix is well within reach.',

      stepEyebrow: 'Recommended first',
      stepTitle: 'Act soon — start with a certified specialist',
      stepBody:
        'At this level, the recommended path is to begin mitigation promptly rather than wait on more testing. A ' +
        'certified specialist can confirm your situation and install a system that brings the number down fast. If ' +
        'you’d like a quick second reading first, we can rush you a short-term kit — but don’t let ' +
        'testing delay the fix.',
      stepCta: 'Send me a follow-up kit',

      proTitle: 'A reading this high warrants a certified specialist soon',
      proBody:
        'They’ll assess your home and install a system that typically reduces radon by <strong>50–99%</strong>, ' +
        'often back to low single digits. There’s no obligation, and we don’t charge for the introduction.'
    }
  ];

  var TEST_TYPES = {
    detector_reading: 'Radon Detector Reading',
    test_reading: 'Radon Test Reading'
  };

  /* ---- Tier resolution -------------------------------------------------- */

  function tierForLevel(level) {
    var n = parseFloat(level);
    if (!isFinite(n) || n < 0) return null;
    for (var i = 0; i < TIERS.length; i++) {
      if (n >= TIERS[i].min && n < TIERS[i].max) return TIERS[i];
    }
    return TIERS[TIERS.length - 1];
  }

  /*
    Horizontal position of the reading on the six-band gauge, as a percentage.
    Each band occupies 1/6 of the width; the reading is placed proportionally
    inside its own band. The open-ended top tier is compressed onto 20-50 pCi/L
    so a 200 pCi/L reading still renders inside the bar.
  */
  function gaugePercent(level) {
    var n = parseFloat(level);
    var tier = tierForLevel(n);
    if (!tier) return 0;
    var index = TIERS.indexOf(tier);
    var span = tier.max === Infinity ? 30 : tier.max - tier.min;
    var fraction = (n - tier.min) / span;
    if (fraction < 0.06) fraction = 0.06;
    if (fraction > 0.94) fraction = 0.94;
    return ((index + fraction) / TIERS.length) * 100;
  }

  /*
    Formats 5 -> "5.0", 0.5 -> "0.5", 12.25 -> "12.3".

    Guards against a rounding lie: 3.99 is Elevated (below the EPA action level),
    but "4.0" reads as High. When one decimal would round the reading across a
    tier boundary, keep the second decimal so the number and the tier agree.
  */
  function formatLevel(level) {
    var n = parseFloat(level);
    if (!isFinite(n)) return '';
    var oneDecimal = n.toFixed(1);
    var trueTier = tierForLevel(n);
    var shownTier = tierForLevel(parseFloat(oneDecimal));
    if (trueTier && shownTier && trueTier.key !== shownTier.key) {
      return n.toFixed(2);
    }
    return oneDecimal;
  }

  function testTypeLabel(value) {
    return TEST_TYPES[value] || value || '';
  }

  global.RadonTiers = {
    EPA_ACTION_LEVEL: EPA_ACTION_LEVEL,
    LEVEL_TOKEN: LEVEL_TOKEN,
    CIGARETTE_FOOTNOTE: CIGARETTE_FOOTNOTE,
    PULL_QUOTE: PULL_QUOTE,
    TIERS: TIERS,
    TEST_TYPES: TEST_TYPES,
    tierForLevel: tierForLevel,
    gaugePercent: gaugePercent,
    formatLevel: formatLevel,
    testTypeLabel: testTypeLabel
  };
})(window);
